import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { adminApi } from "@/lib/api";
import { formatApiError } from "@/admin/AdminAuthContext";
import { PageHeader, LoadingRow, Drawer, Field, FieldTextarea, Checkbox } from "@/admin/pages/AdminSubsidiaries";
import { CLIENTS } from "@/lib/content";

const EMPTY = { company: "", quote: "", logo_slug: "", display_order: 100, active: true };

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/testimonials");
      setItems(data);
    } catch (err) { toast.error(formatApiError(err)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    try {
      if (!editing.id) {
        await adminApi.post("/admin/testimonials", editing);
        toast.success("Testimonial added.");
      } else {
        const { id, ...body } = editing;
        await adminApi.patch(`/admin/testimonials/${id}`, body);
        toast.success("Updated.");
      }
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await adminApi.delete(`/admin/testimonials/${id}`);
      toast.success("Deleted.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-testimonials-page">
      <PageHeader title="Testimonials" onAdd={() => setEditing({ ...EMPTY })} testId="add-testimonial" />
      {loading ? <LoadingRow /> : (
        <div className="bg-white border border-softgray divide-y divide-softgray">
          {items.map((t) => (
            <div key={t.id} className="p-5 hover:bg-offwhite/70" data-testid={`testimonial-row-${t.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-navy">{t.company}</div>
                  <p className="text-mutedink text-sm mt-2 line-clamp-2">{t.quote}</p>
                  <div className="text-mutedink text-[11px] mt-2 uppercase tracking-widest">order {t.display_order} · {t.active ? "active" : "hidden"} · logo: {t.logo_slug || "—"}</div>
                </div>
                <button className="btn-outline-gold !py-1.5 !text-[10px]" onClick={() => setEditing(t)}>Edit</button>
                <button className="btn-outline-gold !py-1.5 !text-[10px] !border-red-300 !text-red-700" onClick={() => remove(t.id)}><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
          {items.length === 0 && <div className="p-6 text-mutedink text-sm text-center">No testimonials yet.</div>}
        </div>
      )}
      {editing && (
        <Drawer title={editing.id ? "Edit testimonial" : "New testimonial"}
                onClose={() => setEditing(null)} onSave={save} testId="testimonial-drawer">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Company" required value={editing.company}
                   onChange={(v) => setEditing({ ...editing, company: v })} />
            <div>
              <label className="block text-navy text-xs uppercase tracking-[0.18em] font-semibold mb-2">Logo (client slug)</label>
              <select value={editing.logo_slug} onChange={(e) => setEditing({ ...editing, logo_slug: e.target.value })}
                      className="w-full border border-softgray px-3 py-2 text-navy text-sm outline-none focus:border-gold">
                <option value="">— none —</option>
                {CLIENTS.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
            <FieldTextarea label="Quote" rows={5} value={editing.quote}
                           onChange={(v) => setEditing({ ...editing, quote: v })} className="sm:col-span-2" />
            <Field label="Display order" type="number" value={editing.display_order}
                   onChange={(v) => setEditing({ ...editing, display_order: parseInt(v || "0", 10) })} />
            <Checkbox label="Active" checked={editing.active}
                      onChange={(v) => setEditing({ ...editing, active: v })} />
          </div>
        </Drawer>
      )}
    </div>
  );
}
